import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeftRight, Copy, Check, Loader2, Languages, Sparkles, FileText, Upload, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { TRANSLATION_LANGUAGES } from "../data/translationLanguages";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const MAX_CHARS = 8000;

export default function TranslationHub() {
  const { user } = useAuth();
  const [source, setSource] = useState("en");
  const [target, setTarget] = useState("sn"); // Shona by default (user's requested example)
  const [dialect, setDialect] = useState("");
  const [text, setText] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [fileName, setFileName] = useState("");
  const fileRef = useRef(null);

  const swap = () => {
    setSource(target);
    setTarget(source);
    setText(output);
    setOutput(text);
  };

  const onTranslate = async () => {
    if (!text.trim()) {
      toast.error("Please enter some text to translate");
      return;
    }
    if (text.length > MAX_CHARS) {
      toast.error(`Text too long. Max ${MAX_CHARS.toLocaleString()} characters per request.`);
      return;
    }
    setLoading(true);
    setOutput("");
    try {
      const sourceName = TRANSLATION_LANGUAGES.find((l) => l.code === source)?.name || source;
      const targetName = TRANSLATION_LANGUAGES.find((l) => l.code === target)?.name || target;
      const { data } = await api.post("/translate", {
        text,
        source_lang: sourceName,
        target_lang: targetName,
        dialect_note: dialect.trim(),
      });
      setOutput(data.translated_text || "");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Translation failed");
    } finally {
      setLoading(false);
    }
  };

  const onCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy to clipboard");
    }
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1.5 * 1024 * 1024) {
      toast.error("File too large. Max 1.5 MB for in-browser text extraction.");
      return;
    }
    try {
      const txt = await f.text();
      setText(txt.slice(0, MAX_CHARS));
      setFileName(f.name);
      if (txt.length > MAX_CHARS) {
        toast.warning(`File trimmed to first ${MAX_CHARS.toLocaleString()} chars per request.`);
      }
    } catch {
      toast.error("Could not read file");
    }
  };

  const clearFile = () => {
    setFileName("");
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid="translation-hub-page">
      <div className="max-w-7xl mx-auto px-6">
        {/* Breadcrumb */}
        <nav className="text-xs text-slate-500 flex items-center gap-2 mb-6">
          <Link to="/" className="hover:text-white transition-colors">Home</Link>
          <span>/</span>
          <span className="text-[#B8D0EE]">Translation Hub</span>
        </nav>

        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#D4AF37]/40 bg-[#D4AF37]/5 mb-6 notranslate">
              <Sparkles className="w-3.5 h-3.5 text-[#D4AF37]" />
              <span className="text-[10px] uppercase tracking-[0.25em] font-semibold" style={{ color: "#D4AF37" }}>
                Translation Hub · Claude Sonnet
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98]" data-testid="hub-title">
              Translate any language. <br />
              <span className="text-[#7DA3D9]">Any dialect. Any contract.</span>
            </h1>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-300 leading-relaxed text-lg">
              Built for international rights & royalty negotiations. Translate emails, contracts,
              statements, and statements of work between <span className="text-[#B8D0EE] font-medium">90+ languages</span> —
              including rare combinations like <span className="notranslate font-semibold">Mandarin → Shona</span>{" "}
              or <span className="notranslate font-semibold">Yoruba → Quechua</span>.
            </p>
          </div>
        </div>

        {/* Translator */}
        <div className="glass-card p-6 lg:p-8 mb-10" data-testid="translator-card">
          {/* Language selectors */}
          <div className="grid lg:grid-cols-[1fr_auto_1fr] gap-4 items-center mb-6">
            <div>
              <label className="overline block mb-2">From</label>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value)}
                className="input-dark w-full notranslate"
                data-testid="source-lang"
              >
                <option value="auto">Auto-detect</option>
                {TRANSLATION_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.native} — {l.name}
                  </option>
                ))}
              </select>
            </div>
            <button
              onClick={swap}
              className="mt-6 lg:mt-7 mx-auto inline-flex items-center justify-center w-10 h-10 rounded-md border border-white/10 bg-white/[0.02] hover:border-[#7DA3D9]/40 hover:bg-[#7DA3D9]/5 text-slate-300 hover:text-white transition-colors"
              title="Swap languages"
              data-testid="swap-langs"
            >
              <ArrowLeftRight className="w-4 h-4" />
            </button>
            <div>
              <label className="overline block mb-2">To</label>
              <select
                value={target}
                onChange={(e) => setTarget(e.target.value)}
                className="input-dark w-full notranslate"
                data-testid="target-lang"
              >
                {TRANSLATION_LANGUAGES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.native} — {l.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Optional dialect note */}
          <div className="mb-6">
            <label className="overline block mb-2">Optional · Dialect, register, or regional variant</label>
            <input
              value={dialect}
              onChange={(e) => setDialect(e.target.value)}
              className="input-dark w-full"
              placeholder='e.g. "Zimbabwean Shona", "Cantonese (HK)", "Brazilian Portuguese (formal)", "Andean Quechua"'
              data-testid="dialect-input"
            />
          </div>

          {/* Source + target textareas */}
          <div className="grid lg:grid-cols-2 gap-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Source text</label>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-slate-500">
                    {text.length.toLocaleString()} / {MAX_CHARS.toLocaleString()}
                  </span>
                  <input
                    ref={fileRef}
                    type="file"
                    accept=".txt,.md,.csv,.json,.xml,.html,.rtf,.log"
                    className="hidden"
                    onChange={onFile}
                    data-testid="file-input"
                  />
                  <button
                    onClick={() => fileRef.current?.click()}
                    className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-[#7DA3D9] uppercase tracking-[0.15em]"
                    data-testid="upload-doc-btn"
                  >
                    <Upload className="w-3 h-3" /> Upload .txt
                  </button>
                </div>
              </div>
              {fileName && (
                <div className="mb-2 inline-flex items-center gap-2 px-2.5 py-1 rounded-md border border-[#7DA3D9]/25 bg-[#7DA3D9]/5 text-xs text-[#B8D0EE]">
                  <FileText className="w-3 h-3" />
                  <span className="notranslate">{fileName}</span>
                  <button onClick={clearFile} className="hover:text-white" aria-label="Remove file">
                    <X className="w-3 h-3" />
                  </button>
                </div>
              )}
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
                placeholder="Paste the contract, email, royalty statement, or message you need translated…"
                className="input-dark w-full min-h-[280px] resize-y leading-relaxed"
                data-testid="source-text"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="overline">Translation</label>
                {output && (
                  <button
                    onClick={onCopy}
                    className="inline-flex items-center gap-1 text-[10px] text-slate-400 hover:text-[#7DA3D9] uppercase tracking-[0.15em]"
                    data-testid="copy-output"
                  >
                    {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                )}
              </div>
              <div
                className="input-dark w-full min-h-[280px] whitespace-pre-wrap leading-relaxed text-slate-100 overflow-auto"
                data-testid="output-text"
              >
                {loading ? (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Translating with Claude Sonnet…
                  </div>
                ) : output ? (
                  output
                ) : (
                  <span className="text-slate-500">The translation will appear here.</span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500 max-w-2xl">
              Powered by Claude Sonnet. Proper nouns, agency abbreviations (ASCAP, JASRAC, SACEM…), URLs, phone
              numbers, and email addresses are preserved verbatim.
            </p>
            <button
              onClick={onTranslate}
              disabled={loading || !text.trim()}
              className="btn-gold inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="translate-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Languages className="w-4 h-4" />}
              Translate
            </button>
          </div>
        </div>

        {/* Use-case row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="use-cases">
          {[
            {
              t: "Cross-border contracts",
              d: "Translate publishing, sync, and master-rights agreements between any two jurisdictions before signing.",
            },
            {
              t: "Royalty statement clarity",
              d: "Decode quarterly statements from foreign PROs (GEMA, SACEM, JASRAC) into your native language.",
            },
            {
              t: "Negotiation correspondence",
              d: "Communicate with overseas agents and label legal teams without losing nuance — including rare dialects like Shona, Quechua, or Yoruba.",
            },
          ].map((u) => (
            <div key={u.t} className="glass-card p-6" data-testid={`use-case-${u.t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <div className="text-[#7DA3D9] text-xs uppercase tracking-[0.2em] font-semibold">
                {u.t}
              </div>
              <p className="text-slate-300 mt-3 text-sm leading-relaxed">{u.d}</p>
            </div>
          ))}
        </div>

        {/* CTA — only shown to non-paid users (post-paywall users never see this) */}
        {!user?.has_complete_access && (
          <section className="glass-card p-10 lg:p-14 text-center" data-testid="hub-cta">
            <div className="overline" style={{ color: "#D4AF37" }}>Limited time · only $6.95 to join</div>
            <h2 className="font-display text-3xl lg:text-5xl mt-4 leading-tight">
              Translate. Negotiate. Collect — globally.
            </h2>
            <p className="text-slate-300 mt-5 max-w-2xl mx-auto">
              Unlock the 200+ agency directory for $6.95 lifetime access. Pair it with the
              Translation Hub for end-to-end international rights management.
            </p>
            <div className="mt-10">
              <Link to="/upgrade" className="btn-gold inline-flex items-center gap-2" data-testid="hub-cta-unlock">
                Unlock for $6.95 <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
